import * as React from 'react';
import { Config, yAxesOf, uiText } from '../helpers';
import GroupDropdown from './GroupDropdown';
import { FilterButton } from '../../faceted-search/react/helper';
import Offcanvas from '../../faceted-search/react/Offcanvas';
import OffcanvasContent from './OffcanvasContent';
import ChartErrorBoundary from './ChartErrorBoundary';
import DataTable from './DataTable';
import { ChartBridgeProvider, useChartBridge } from './ChartBridgeContext';
import { FilterDisplayContext } from './FilterDisplayContext';
import { readChartRows, rowsToCsv } from './chartRows';
import { getSharedParam } from './shareParams';
import { downloadChartImage, downloadTextFile, slugifyFilename } from './chartExport';
import {
  DropdownContent,
  DropdownItem,
  DropdownRoot,
  DropdownTrigger,
  SegmentedControlInput,
  SegmentedControlItem,
  SegmentedControlRoot,
  Select
} from '@nvidia/foundations-react-core';
import { ThemeProvider } from '@nvidia/foundations-react-core/ThemeProvider';

// Mirror the page's color scheme (<html data-color-scheme="dark|light">) into the
// Kaizen ThemeProvider so the toolbar controls match the page, and follow runtime
// toggles. Defaults to light when the attribute is absent or unrecognized.
function usePageColorScheme() {
  const read = () => {
    if (typeof document === 'undefined') return 'light';
    return document.documentElement.getAttribute('data-color-scheme') === 'dark' ? 'dark' : 'light';
  };
  const [scheme, setScheme] = React.useState(read);
  React.useEffect(() => {
    const sync = () => setScheme(read());
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-color-scheme'] });
    sync();
    return () => observer.disconnect();
  }, []);
  return scheme;
}

// Download dropdown. Lives inside ChartBridgeProvider (rendered within
// ChartLayout's tree) so it can read the export subtitle the chart publishes;
// PNG/SVG rasterize the chart with a title/subtitle header, CSV hits the
// server-rendered raw-row endpoint.
function DownloadSelect({ chartViewRef, title, filenameBase, imageFooter, viewMode, collectionName }) {
  const { subtitle, rows } = useChartBridge();

  // The chart is unmounted in Table view, so there is no `.recharts-wrapper` to
  // rasterize — disable the image formats rather than let them no-op silently.
  // CSV stays available: it exports rows, which is exactly what Table view shows.
  const imageDisabled = viewMode !== 'chart';

  const handleDownload = (value) => {
    if (value === 'csv') {
      // The CURRENT SELECTION, generated client-side. Deliberately no fall back
      // to the server's /search-data endpoint: that returns the full unfiltered
      // export, a different product for a different audience (the machine-
      // readable link on the .md page). Substituting it would silently hand the
      // user every row — worst of all when they filtered to zero and expect an
      // empty file.
      //
      // null means no selection was ever published (Typesense mounts no
      // ResultsBridge), so use the injected export — matching DataTable, which
      // would otherwise show rows the CSV omitted. An empty ARRAY is a real
      // empty selection and must export as empty.
      const csvRows = rows ?? readChartRows(collectionName) ?? [];
      downloadTextFile(rowsToCsv(csvRows), `${filenameBase}.csv`, 'text/csv;charset=utf-8');
      return;
    }
    if (value !== 'png' && value !== 'svg') return;
    const wrapper = chartViewRef.current?.querySelector('.recharts-wrapper');
    if (!wrapper) {
      // Unreachable via the UI (the items are disabled off the chart view); warn
      // so a future regression is diagnosable instead of silently doing nothing.
      console.warn('nv-chart: no chart to export — .recharts-wrapper not found');
      return;
    }
    downloadChartImage(wrapper, {
      format: value, filename: filenameBase, title, subtitle,
      footer: imageFooter,
    });
  };

  return (
    <DropdownRoot>
      <DropdownTrigger showChevron kind="secondary" size="medium" color='brand'>
        {uiText('download', 'Download')}
      </DropdownTrigger>
      <DropdownContent align="end">
        <DropdownItem disabled={imageDisabled} onSelect={() => handleDownload('png')}>{uiText('download_png', 'Chart (PNG)')}</DropdownItem>
        <DropdownItem disabled={imageDisabled} onSelect={() => handleDownload('svg')}>{uiText('download_svg', 'Chart (SVG)')}</DropdownItem>
        <DropdownItem onSelect={() => handleDownload('csv')}>{uiText('download_csv', 'Table (CSV)')}</DropdownItem>
      </DropdownContent>
    </DropdownRoot>
  );
}

// Reports which fields carry data in the CURRENT filtered rows (read from the
// bridge, published by ResultsBridge) up to ChartLayout, so the Y-Axis switcher
// can hide metrics that have no data for the active selection — not only metrics
// absent dataset-wide. Reports null when there are no rows yet (falls back to the
// dataset-wide payload check). A field key guards against redundant updates.
function YAxisDataProbe({ onFields }) {
  const { rows } = useChartBridge();
  const lastKey = React.useRef(null);
  React.useEffect(() => {
    if (!rows || rows.length === 0) {
      if (lastKey.current !== null) { lastKey.current = null; onFields(null); }
      return;
    }
    const present = new Set();
    rows.forEach(row => { if (row) Object.keys(row).forEach(k => { if (row[k] != null) present.add(k); }); });
    const key = [...present].sort().join(',');
    if (key !== lastKey.current) { lastKey.current = key; onFields(present); }
  }, [rows, onFields]);
  return null;
}

// Shared layout shell for both TypesenseProvider and OpenSearchProvider.
// Each provider supplies its own search-context wrapper around this component
// and passes the provider-specific render slots.
//
// Naming note: `groupLabel` (string) is the currently-selected group's label
// for the dropdown. `renderGroupLabel` is a render slot for the breadcrumb
// strip that shows active filter values. They are unrelated.
export default function ChartLayout({
  css,
  title,
  currentGroup,
  groups,
  groupLabel,
  handleGroupChange,
  renderFilter,
  renderGroupLabel,
  renderHits,
  renderShare = null,
  renderResultsBridge = null,
  collectionName = null,
  clearAllOuter = null,
  clearAllSidebar = null,
  // Shared-permalink control params, captured by the provider before the URL is
  // stripped (reading them here directly races that strip). Null when absent.
  initialAxis = null,
  initialYField = null,
  initialViewMode = null,
  // Chart-level: render sidebar filters as collapsible sections.
  collapsibleFilters = false,
  // Chart-level footer stamped onto downloaded PNG/SVG images.
  // Chart-wide footer for downloaded images: { source, disclaimer }.
  imageFooter = {},
}) {
  const hasFilters = currentGroup.filters?.length > 0;
  // Toolbar controls are opt-in per chart (default off) — see groupDefaults.
  const toolbar = currentGroup.toolbar || {};
  const filterNodes = hasFilters ? currentGroup.filters.map(renderFilter) : null;

  const chartViewRef = React.useRef(null);
  // Unique per chart instance so the Y-Axis label/select association holds when
  // several charts render on one page (a static id would collide).
  const yAxisId = React.useId();

  // Selectable Y metrics for this group (config-driven; one entry when the
  // group defines a single axes.y). Clamp the selection so switching to a group
  // with fewer options never lands out of range.
  const yAxes = React.useMemo(() => yAxesOf(currentGroup), [currentGroup]);

  // Hide Y metrics with no data (§1.1). Prefer the CURRENT filtered rows (from the
  // bridge, captured by YAxisDataProbe) so a metric absent from the active
  // selection drops out of the switcher — not only one absent dataset-wide. Fall
  // back to the injected payload before any rows arrive; never hide everything.
  const [dataYFields, setDataYFields] = React.useState(null);
  const payloadRows = React.useMemo(() => readChartRows(collectionName) || [], [collectionName]);
  const availableYAxes = React.useMemo(() => {
    const hasData = (field) => {
      if (dataYFields) return dataYFields.has(field);
      if (payloadRows.length === 0) return true;
      return payloadRows.some(row => row?.[field] != null);
    };
    const withData = yAxes.filter(axis => hasData(axis.field));
    return withData.length > 0 ? withData : yAxes;
  }, [yAxes, payloadRows, dataYFields]);

  // Initial view / Y-axis come from a shared permalink when present (§1.4).
  // The Y selection is keyed by field so filtering the list never mis-points it.
  const [shareCopied, setShareCopied] = React.useState(false);
  const [viewMode, setViewMode] = React.useState(() => ((initialViewMode ?? getSharedParam('view')) === 'table' ? 'table' : 'chart'));
  // Theme for the Kaizen toolbar controls — follows <html data-color-scheme>.
  const colorScheme = usePageColorScheme();
  const [selectedYField, setSelectedYField] = React.useState(() => (initialYField ?? getSharedParam('y')) || null);
  const selectedYAxis = availableYAxes.find(axis => axis.field === selectedYField) || availableYAxes[0];

  // Human-readable Table headers: map a raw index field to the label the chart
  // config already defines for it (axis labels + every Y option + facet/filter
  // labels). Fields without a configured label fall back to a prettified key in
  // DataTable.
  const fieldLabels = React.useMemo(() => {
    const map = {};
    const add = (field, label) => { if (field && label) map[field] = label; };
    add(currentGroup.axes?.x?.field, currentGroup.axes?.x?.label);
    yAxes.forEach(y => add(y?.field, y?.label));
    (currentGroup.filters || []).forEach(f => add(f.attribute, f.label));
    return map;
  }, [currentGroup, yAxes]);

  // Exported image title: "<metric> vs. Interactivity" (README 1.1).
  const exportTitle = selectedYAxis?.label ? `${selectedYAxis.label} vs. Interactivity` : title;

  const handleShare = () => {
    const clip = typeof navigator !== 'undefined' && navigator.clipboard;
    if (!clip || !clip.writeText) return;
    clip.writeText(window.location.href).then(
      () => {
        setShareCopied(true);
        window.setTimeout(() => setShareCopied(false), 2000);
      },
      () => { /* clipboard denied — leave the label unchanged */ }
    );
  };

  return (
    <FilterDisplayContext.Provider value={{ collapsible: collapsibleFilters }}>
    <ChartBridgeProvider initialSelected={initialAxis ?? getSharedParam('axis')}>
    {/* Publishes the filtered rows to the context, regardless of Chart/Table mode. */}
    {renderResultsBridge?.()}
    <YAxisDataProbe onFields={setDataYFields}/>
    <div className={css.root}>
      {clearAllOuter}
      <Offcanvas title='Filters' callback={() => {}}>
        <OffcanvasContent>
          <div className='nv-chart__filters'>
            <GroupDropdown groupLabel={groupLabel} groups={groups} valueSetter={handleGroupChange}/>
            {filterNodes}
          </div>
        </OffcanvasContent>
      </Offcanvas>
      <div className='container'>
        <div className='row'>
          <div className='col-lg-3 d-none d-lg-block d-xl-block d-xxl-block'>
            <div className='nv-chart__filters desktop-filters'>
              {clearAllSidebar}
              <GroupDropdown groupLabel={groupLabel} groups={groups} valueSetter={handleGroupChange}/>
              <h3 className='h--smaller my-0 nv-chart__filters-heading'>{Config.I18n.Filters.Title}</h3>
              {filterNodes}
            </div>
          </div>
          <div className='col-lg-9'>
            <div className='nv-chart__header'>
              <h3 className='h--small my-0'>{title}</h3>
              <FilterButton/>

            </div>
            <div className="nv-chart__actions">
              {/* One ThemeProvider for the whole action bar (Y-Axis + toolbar) so the
                  Kaizen controls read theme/density from it; display:contents keeps the
                  flex rows intact. */}
              <ThemeProvider theme={colorScheme} density='standard' style={{ display: 'contents' }}>
                <div className='actions actions__primary'>
                  {availableYAxes.length > 1 && (
                    <div className='action'>
                      <label id={yAxisId} className='action__label'>{uiText('y_axis', 'Y-Axis:')}</label>
                      <Select
                        size='medium'
                        color='brand'
                        value={selectedYAxis?.field ?? ''}
                        onValueChange={setSelectedYField}
                        aria-labelledby={yAxisId}
                        triggerKind='flat'
                        items={availableYAxes.map(axis => ({ value: axis.field, children: axis.label || axis.field }))}
                      />
                    </div>
                  )}
                </div>
                <div className='actions actions__secondary'>
                  {toolbar.viewToggle && (
                    <SegmentedControlRoot
                      size='medium'
                      name='nv-chart-view'
                      color='brand'
                      aria-label={uiText('view_mode', 'View mode')}
                      onValueChange={setViewMode}
                    >
                      <SegmentedControlItem>
                        <SegmentedControlInput value='chart' defaultChecked={viewMode === 'chart'}/>
                        {uiText('chart', 'Chart')}
                      </SegmentedControlItem>
                      <SegmentedControlItem>
                        <SegmentedControlInput value='table' defaultChecked={viewMode === 'table'}/>
                        {uiText('table', 'Table')}
                      </SegmentedControlItem>
                    </SegmentedControlRoot>
                  )}

                  {toolbar.download && (
                    <DownloadSelect
                      chartViewRef={chartViewRef}
                      title={exportTitle}
                      filenameBase={slugifyFilename(title)}
                      imageFooter={imageFooter}
                      viewMode={viewMode}
                      collectionName={collectionName}
                    />
                  )}

                  {toolbar.share && (renderShare
                    ? renderShare({ groupLabel, viewMode, yField: selectedYAxis?.field })
                    : (
                      <>
                        <button
                          type='button'
                          className='nv-cta-primary-alt nv-cta-primary-alt--small'
                          onClick={handleShare}
                        >
                          {shareCopied ? uiText('copied', 'Copied!') : uiText('share', 'Share')}
                        </button>
                        {/* A button's own label change isn't announced; a polite live region is. */}
                        <span className='visually-hidden' aria-live='polite'>
                          {shareCopied ? uiText('link_copied', 'Link copied to clipboard') : ''}
                        </span>
                      </>
                    ))}
                </div>
              </ThemeProvider>
            </div>
            <div className='nv-chart__view' ref={chartViewRef}>
              <div className='text-center mb-3'>
                <div className='nv-chart__group-description'>
                  <h3 className='h--smallest'>{currentGroup.description}</h3>
                </div>
                <div className='nv-chart__group-label'>
                  {renderGroupLabel?.()}
                </div>
              </div>
              <ChartErrorBoundary>
                {viewMode === 'table'
                  ? <DataTable collectionName={collectionName} fieldLabels={fieldLabels}/>
                  : renderHits(selectedYAxis)}
              </ChartErrorBoundary>
            </div>
          </div>
        </div>
      </div>
    </div>
    </ChartBridgeProvider>
    </FilterDisplayContext.Provider>
  );
}
